import React from "react";
import { Link } from "react-router-dom";
import MarketingNav from "@/components/MarketingNav";
import Footer from "@/components/Footer";
import { Check } from "lucide-react";

const TIERS = [
  {
    name: "Solo",
    price: "Flat per-country fee",
    desc: "For first-time founders & indie hackers. Pay once per incorporation, no subscriptions required.",
    features: ["All 7 jurisdictions available", "AI jurisdiction advisor", "Guided incorporation flow", "Document vault (1GB)", "Email support"],
    cta: "Start free",
  },
  {
    name: "Compliance",
    price: "$49 /month per entity",
    desc: "The recurring compliance engine. We track every deadline your company owes across every geography.",
    features: ["Everything in Solo", "Auto-generated filing calendar", "VAT / GST / Tax reminders", "Director KYC tracking", "Partner assignment & SLA", "Priority support"],
    featured: true,
    cta: "Start trial",
  },
  {
    name: "Enterprise",
    price: "Custom",
    desc: "For global groups with subsidiaries across jurisdictions. Cap-table sync, consolidated compliance, dedicated partner manager.",
    features: ["Multi-entity dashboard", "Group compliance reports", "Dedicated partner success manager", "SSO + audit logs", "Custom MSA", "24/7 escalation"],
    cta: "Talk to sales",
  },
];

const Pricing = () => (
  <div className="bg-surface-app min-h-screen text-ink-body">
    <MarketingNav />
    <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 pt-16 pb-12">
      <div className="text-xs uppercase tracking-[0.28em] text-ink-muted font-mono-accent mb-6">Pricing</div>
      <h1 className="h-display text-5xl md:text-7xl">
        Pay once to <span className="italic text-artha-amber">exist</span>. <br />
        Subscribe to <span className="italic">stay alive</span>.
      </h1>
      <p className="mt-6 text-lg text-ink-muted max-w-2xl">
        One-time incorporation fees vary by country. Ongoing compliance is priced per entity, cancel anytime.
      </p>
    </section>

    <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 pb-24 grid grid-cols-1 md:grid-cols-3 gap-px bg-artha-light-border border border-border-subtle">
      {TIERS.map((t) => (
        <div key={t.name}
          data-testid={`pricing-tier-${t.name.toLowerCase()}`}
          className={`p-10 flex flex-col ${t.featured ? "bg-artha-ink text-artha-cream" : "bg-surface-app"}`}>
          <div className={`text-xs uppercase tracking-[0.28em] font-mono-accent ${t.featured ? "text-artha-amber" : "text-ink-muted"}`}>
            {t.featured ? "Most chosen" : "Plan"}
          </div>
          <div className="h-display text-4xl md:text-5xl mt-4">{t.name}</div>
          <div className={`mt-4 text-sm font-mono-accent ${t.featured ? "text-artha-amber" : "text-ink-body"}`}>{t.price}</div>
          <p className={`mt-4 text-sm leading-relaxed ${t.featured ? "text-white/70" : "text-ink-muted"}`}>{t.desc}</p>
          <ul className="mt-8 space-y-3 flex-1">
            {t.features.map((f) => (
              <li key={f} className="flex items-start gap-3 text-sm">
                <Check size={16} className={t.featured ? "text-artha-amber mt-0.5" : "text-ink-body mt-0.5"} />
                <span className={t.featured ? "text-white/85" : "text-ink-body"}>{f}</span>
              </li>
            ))}
          </ul>
          <Link to="/signup" data-testid={`pricing-${t.name.toLowerCase()}-cta`}
            className={`mt-10 inline-flex items-center justify-center px-6 py-3.5 text-sm tracking-tight transition-colors
            ${t.featured
              ? "bg-artha-amber text-ink-body hover:bg-surface-app"
              : "border border-artha-ink hover:bg-artha-ink hover:text-artha-cream"}`}>
            {t.cta}
          </Link>
        </div>
      ))}
    </section>

    <Footer />
  </div>
);

export default Pricing;
